import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import {
  ShoppingBag,
  Truck,
  CreditCard,
  ArrowLeft,
  ArrowLeftCircle,
  MessageSquare,
  Pencil,
} from "lucide-react";
import { fadeUp, slideLeft, slideRight } from "animations/variants";
import { ChevronDown } from "lucide-react";
import { setAuthModalOpen } from "store/features/authModalSlice";
import { useNavigate } from "react-router-dom";
import SignInSection from "components/CheckOut/SignInSection";
import CheckoutForm from "components/CheckOut/CheckoutForm";
import userApi from "api/user/user.api";
import { setNote } from "store/features/cartSlice";

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isDesktop;
};

const Checkout = () => {
  const cart = useSelector((state) => state.cart.cart);
  const user = useSelector((state) => state.user.user);
  const note = useSelector((state) => state.cart.note);
  const tip = useSelector((state) => state.cart.tip);
  // console.log({ cart });
  // console.log({ note });

  const subtotal = cart.reduce((acc, item) => acc + item.price * item.count, 0);

  const deliveryFee = 0;
  const total = subtotal + deliveryFee;
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  const [operators, setOperators] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [isEditingNote, setIsEditingNote] = useState(false);
  const [tempNote, setTempNote] = useState(note);

  useEffect(() => {
    setTempNote(note);
  }, [note]);

  useEffect(() => {
    let isMounted = true;

    const fetchOperators = async () => {
      try {
        setIsLoading(true);

        const { response, err } = await userApi.getAllOperator();

        if (!isMounted) return;

        if (response?.data) {
          setOperators(response.data);
        }

        if (err) {
          console.error("Failed to load operators:", err);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Unexpected error:", error);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchOperators();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <section className="min-h-screen bg-bgdry text-navy pt-28 pb-20 px-6">
      <div className="text-center mb-6 lg:mb-14">
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          className="text-2xl md:text-3xl font-heading text-neonYellow animate-neonFlicker my-6 tracking-widest uppercase"
        >
          Checkout
        </motion.h2>

        <motion.div
          variants={fadeUp}
          className="mt-6 flex items-center justify-center gap-4"
        >
          <div className="w-20 border-t border-dashed border-gray-300"></div>
          <span className="w-2 h-2 bg-neonYellow rounded-full"></span>
          <div className="w-20 border-t border-dashed border-gray-300"></div>
        </motion.div>
      </div>
      <div className="min-h-screen ">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 ">
          <motion.div
            variants={slideLeft}
            initial="hidden"
            animate="show"
            className="lg:px-12 lg:py-10 "
          >
            <motion.div
              variants={fadeUp}
              className="p-0 lg:py-8 lg:px-6 rounded-2xl border-0 lg:border border-navy lg:shadow-xl space-y-6"
            >
              {!user ? (
                <SignInSection dispatch={dispatch} />
              ) : (
                <CheckoutForm
                  user={user}
                  total={total}
                  operator={operators[0]}
                  isLoading={isLoading}
                />
              )}
            </motion.div>
            <button
              onClick={() => navigate("/cart")}
              className="items-center gap-2 my-6 mx-auto text-yellowstar hover:text-white transition-all hidden lg:flex"
            >
              <ArrowLeftCircle size={18} />
              <span>Back</span>
            </button>
          </motion.div>

          <motion.div
            variants={slideRight}
            initial="hidden"
            animate="show"
            className="md:px-12 md:py-20  md:border-l border-gray-200 lg:bg-gray-300"
          >
            {cart.length === 0 ? (
              <div className="text-center text-gray-400 py-12">
                <ShoppingBag size={40} className="mx-auto mb-4 opacity-40" />
                <p>Your cart is empty</p>
              </div>
            ) : (
              <>
                {/* ================= MOBILE HEADER ================= */}
                <div
                  className="md:hidden px-0 lg:px-6 py-4 flex items-center justify-between cursor-pointer mt-6 lg:mt-0 lg:border-b border-gray-300"
                  onClick={() => setOpen(!open)}
                >
                  {!open ? (
                    <div className="flex items-center gap-4">
                      <div className="relative flex items-center">
                        {cart.slice(0, 2).map((item, index) => (
                          <img
                            key={index}
                            src={
                              item.image || "/saigonstar/saigonstar_sqr.webp"
                            }
                            alt="preview"
                            className="w-12 h-12 rounded-lg object-cover border border-white shadow-md bgd-darkbg"
                            style={{
                              marginLeft: index === 0 ? 0 : -36,
                              zIndex: 10 + index,
                              transform:
                                index === 0 ? "rotate(-6deg)" : "rotate(8deg)",
                            }}
                          />
                        ))}

                        {cart.length > 2 && (
                          <div
                            className="w-12 h-12 rounded-lg bg-gray-200 text-gray-700 flex items-center justify-center text-xs font-semibold border border-white shadow-md"
                            style={{
                              marginLeft: -36,
                              zIndex: 0,
                              transform: "rotate(12deg)",
                            }}
                          >
                            +{cart.length - 2}
                          </div>
                        )}
                      </div>

                      {/* TEXT */}
                      <div>
                        <p className="font-semibold">Total</p>
                        <p className="text-sm text-gray-500">
                          {cart.length} item{cart.length > 1 && "s"}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <h2 className="font-bold">Order Summary</h2>
                  )}

                  <div className="flex items-center gap-3">
                    {!open && (
                      <>
                        {" "}
                        <span className="text-xs bg-gray-200 px-2 py-1 rounded-full">
                          NZD
                        </span>
                        <span className="font-semibold text-lg">
                          ${total.toFixed(2)}
                        </span>
                      </>
                    )}

                    <motion.div
                      animate={{ rotate: open ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronDown size={20} />
                    </motion.div>
                  </div>
                </div>

                {/* ================= COLLAPSIBLE CONTENT ================= */}
                <AnimatePresence>
                  {(isDesktop || open) && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className=""
                    >
                      <div className="px-0 lg:px-6 md:px-0 py-6 md:py-0">
                        {/* Cart Items */}
                        <div className="space-y-6">
                          {cart.map((item) => (
                            <div
                              key={item._id}
                              className="flex items-start justify-between"
                            >
                              <div className="flex items-start gap-4">
                                <div className="relative">
                                  <img
                                    src={
                                      item.image ||
                                      "/saigonstar/saigonstar_sqr.webp"
                                    }
                                    alt={item.name}
                                    className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                                  />
                                  <div className="absolute -top-3 -left-3 bg-black text-white text-xs font-semibold w-6 h-6 flex items-center justify-center rounded-full shadow">
                                    {item.count}
                                  </div>
                                </div>

                                <div>
                                  <p className="font-semibold">{item.title}</p>
                                  {item.selectedOption && (
                                    <p className="text-sm text-gray-500 mt-1">
                                      {item.selectedOption}
                                    </p>
                                  )}
                                </div>
                              </div>

                              <div className="font-medium">
                                ${(item.price * item.count).toFixed(2)}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Totals */}
                        <div className="mt-8 space-y-3 text-sm">
                          <div className="flex justify-between text-gray-600">
                            <span>Subtotal</span>
                            <span>${subtotal.toFixed(2)}</span>
                          </div>

                          <div className="flex justify-between text-gray-600">
                            <span>Pickup in store</span>
                            <span>FREE</span>
                          </div>

                          <div className="flex justify-between text-gray-600">
                            <span>Tip</span>
                            <span>{tip}</span>
                          </div>

                          <div className="border-t border-gray-200 pt-4 flex justify-between text-lg font-semibold">
                            <span>Total</span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs bg-gray-200 px-2 py-1 rounded-full">
                                NZD
                              </span>
                              <span>${total.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>

                        {/* ================= NOTE SECTION ================= */}
                        <div className="mt-12 border-t border-gray-200 p-3 flex gap-3">
                          <div className="mt-1 text-gray-400">
                            <MessageSquare size={18} />
                          </div>

                          <div className="flex-1">
                            {/* HEADER */}
                            <div className="flex justify-between items-center mb-1">
                              <p className="text-xs uppercase tracking-widest text-neonYellow animate-neonFlicker">
                                Note
                              </p>

                              {!isEditingNote && (
                                <button
                                  onClick={() => setIsEditingNote(true)}
                                  className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 hover:text-black transition flex items-center gap-1"
                                >
                                  <Pencil size={14} />
                                  {!note && (
                                    <span className="text-xs">Add</span>
                                  )}
                                </button>
                              )}
                            </div>

                            {/* ================= VIEW MODE ================= */}
                            {!isEditingNote ? (
                              note ? (
                                <p className="text-sm text-gray-700 whitespace-pre-line">
                                  {note}
                                </p>
                              ) : (
                                <p className="text-sm text-gray-400 italic">
                                  No note added
                                </p>
                              )
                            ) : (
                              /* ================= EDIT MODE ================= */
                              <div className="space-y-2">
                                <textarea
                                  autoFocus
                                  value={tempNote}
                                  onChange={(e) => setTempNote(e.target.value)}
                                  placeholder="Add note for kitchen (e.g. no onion, less spicy...)"
                                  className="w-full border rounded p-2 text-sm focus:outline-none focus:ring-1 focus:ring-black"
                                />

                                <div className="flex gap-2">
                                  <button
                                    onClick={() => {
                                      dispatch(setNote(tempNote));
                                      setIsEditingNote(false);
                                    }}
                                    className="text-xs px-3 py-1 bg-neonYellow text-navy rounded"
                                  >
                                    Save
                                  </button>

                                  <button
                                    onClick={() => {
                                      setTempNote(note);
                                      setIsEditingNote(false);
                                    }}
                                    className="text-xs px-3 py-1 border rounded"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                <button
                  onClick={() => navigate("/cart")}
                  className="flex items-center gap-2 my-2 mx-auto text-yellowstar hover:text-white transition-all lg:hidden"
                >
                  <ArrowLeftCircle size={18} />
                  <span>Back</span>
                </button>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Checkout;
